import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store';
import PostCard from '../components/post/PostCard';

interface Post {
  id: string;
  content: string;
  authorId: string;
  segmentId?: string;
  weatherData?: any;
  createdAt: string;
  author: {
    id: string;
    displayName: string;
    handle: string;
    avatarUrl?: string;
  };
  media: Array<{
    id: string;
    type: string;
    url: string;
    thumbnailUrl?: string;
  }>;
  segment?: {
    id: string;
    name: string;
    waterway: {
      id: string;
      name: string;
    };
  };
  _count: {
    reactions: number;
    comments: number;
  };
  userReaction?: string;
}

export default function HomePage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | undefined>();
  const { user } = useAuthStore();
  
  const loadPosts = useCallback(async (reset = false) => {
    try {
      const res = await api.posts.getFeed({ cursor: reset ? undefined : cursor });
      const postItems = res.posts || res.items || res;
      if (reset) {
        setPosts(Array.isArray(postItems) ? postItems : []);
      } else {
        setPosts(prev => [...prev, ...(Array.isArray(postItems) ? postItems : [])]);
      }
      setCursor(res.nextCursor);
      setHasMore(!!res.nextCursor);
    } catch (err: any) {
      setError(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [cursor]);
  
  useEffect(() => {
    loadPosts(true);
  }, []);
  
  const handleReaction = async (postId: string, type: string) => {
    try {
      await api.posts.react(postId, { type });
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const hadReaction = !!post.userReaction;
          const sameReaction = post.userReaction === type;
          return {
            ...post,
            userReaction: sameReaction ? undefined : type,
            _count: {
              ...post._count,
              reactions: post._count.reactions + (sameReaction ? -1 : hadReaction ? 0 : 1),
            },
          };
        }
        return post;
      }));
    } catch (err: any) {
      console.error('Failed to react:', err);
    }
  };
  
  const handleDelete = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await api.posts.delete(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err: any) {
      console.error('Failed to delete:', err);
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Feed</h1>
        <Link to="/create" className="btn btn-primary">
          <PlusIcon className="w-5 h-5" />
          <span className="hidden sm:inline ml-2">New Post</span>
        </Link>
      </div>
      
      {/* Ad banner placeholder */}
      {!user?.isSubscribed && (
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4 mb-6 text-center">
          <p className="text-xs text-slate-500 mb-2">Advertisement</p>
          <div className="h-24 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
            <span className="text-slate-400">Ad Space</span>
          </div>
        </div>
      )}
      
      {/* Posts */}
      {loading && posts.length === 0 ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <PostSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={() => loadPosts(true)} className="btn btn-primary">
            Try again
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <PostIcon className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No posts yet</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            Be the first to share something with the community!
          </p>
          <Link to="/create" className="btn btn-primary">Create a post</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post, index) => (
            <>
              <PostCard
                key={post.id}
                post={post}
                onReact={handleReaction}
                onDelete={user?.id === post.authorId ? handleDelete : undefined}
              />
              {/* Native ad every 5 posts */}
              {!user?.isSubscribed && (index + 1) % 5 === 0 && (
                <div className="card p-4">
                  <p className="text-xs text-slate-500 mb-2">Sponsored</p>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-lg flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900 dark:text-white">Ad Title</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        This is a native ad that blends with the content.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ))}
          
          {hasMore && (
            <button
              onClick={() => loadPosts()}
              disabled={loading}
              className="btn btn-secondary w-full"
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="card p-4 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-full" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-2" />
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-24" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded" />
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
      </div>
      <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-lg mt-4" />
    </div>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PostIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  );
}
